import {
  getOwnerStatementSummariesDetailLayout,
  ownerStatementSummariesMonthRangeLabelFormatter,
  type OwnerStatementSummariesDetailData,
} from '@vrplatform/embed';
import { formatOwnerName } from '@vrplatform/ui-common';
import { formatCurrency, toTitleCase } from '@vrplatform/utils';

type PdfField = {
  label: string;
  value: string;
};

type PdfColumn = {
  id: string;
  name: string;
  align?: 'left' | 'right';
};

type PdfRow = Record<string, string>;

type SummaryLine = {
  name: string;
  text: string;
  props?: {
    separated?: boolean;
  };
};

export type OwnerStatementSummaryPdfSection = {
  title: string;
  columns: PdfColumn[];
  rows: PdfRow[];
};

export type OwnerStatementSummaryPdfModel = {
  filename: string;
  monthLabel: string;
  subline: string;
  teamName: string;
  teamLogo?: string | null;
  info: {
    listingAddress?: string | null;
    listingName?: string | null;
    ownerFields: PdfField[];
    teamFields: PdfField[];
  };
  summary: {
    layoutName: string;
    lines: SummaryLine[];
    title: string;
    viewLabel: string;
  };
  sections: OwnerStatementSummaryPdfSection[];
};

const sanitizeFilePart = (value: string | null | undefined) => {
  const sanitized = (value || 'owner-statement-summary')
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  return sanitized || 'owner-statement-summary';
};

const getPdfField = (label: string, value: string | null | undefined) => {
  if (!value?.trim()) {
    return null;
  }

  return {
    label,
    value,
  } satisfies PdfField;
};

const getPdfSection = ({
  section,
  viewLabel,
}: {
  section:
    | OwnerStatementSummariesDetailData['layouts'][number]['netRevenueSection']
    | OwnerStatementSummariesDetailData['layouts'][number]['otherSections'][number]
    | NonNullable<
        OwnerStatementSummariesDetailData['layouts'][number]['payoutsSection']
      >;
  viewLabel: string;
}) => {
  const sampleColumns =
    section.rows[0]?.columns ?? section.subtotal?.columns ?? [];

  if (!sampleColumns.length) {
    return null;
  }

  const columns: PdfColumn[] = [
    {
      id: 'title',
      name: viewLabel,
      align: 'left',
    },
    ...sampleColumns.map((column, index) => ({
      id: column.title || `column-${index + 1}`,
      name: column.title || `Column ${index + 1}`,
      align: 'right' as const,
    })),
  ];

  const rows = section.rows.map<PdfRow>((row) => {
    return {
      title: row.title || '-',
      ...Object.fromEntries(
        row.columns.map((column, index) => [
          column.title || columns[index + 1]?.id || `column-${index + 1}`,
          column.formatted || '',
        ])
      ),
    };
  });

  if (section.subtotal?.columns?.length) {
    rows.push({
      title: section.subtotal.title || 'Total',
      ...Object.fromEntries(
        section.subtotal.columns.map((column, index) => [
          column.title || columns[index + 1]?.id || `column-${index + 1}`,
          column.formatted || '',
        ])
      ),
    });
  }

  return {
    title: section.title || 'Section',
    columns,
    rows,
  } satisfies OwnerStatementSummaryPdfSection;
};

const getSummaryLines = ({
  currency,
  financials,
}: {
  currency: string;
  financials: OwnerStatementSummariesDetailData['financials'];
}): SummaryLine[] => {
  const balanceForward = financials.balanceStart + financials.payouts;
  const reserve = financials.reserve ?? 0;

  return [
    {
      name: 'Balance start',
      text: formatCurrency(financials.balanceStart / 100, currency),
    },
    {
      name: 'Payouts',
      text: formatCurrency(financials.payouts / 100, currency),
    },
    {
      name: 'Balance forward',
      text: formatCurrency(balanceForward / 100, currency),
      props: {
        separated: true,
      },
    },
    {
      name: 'Net revenue',
      text: formatCurrency(financials.netRevenue / 100, currency),
    },
    {
      name: 'Expenses',
      text: formatCurrency(financials.expenses / 100, currency),
    },
    {
      name: 'Balance end',
      text: formatCurrency(financials.balanceEnd / 100, currency),
      props: {
        separated: true,
      },
    },
    ...(reserve !== 0
      ? [
          {
            name: 'Reserve',
            text: formatCurrency(reserve / 100, currency),
          },
          {
            name: 'Available balance',
            text: formatCurrency(financials.currentBalance / 100, currency),
            props: {
              separated: true,
            },
          },
        ]
      : []),
  ];
};

export const getOwnerStatementSummaryPdfModel = ({
  currency,
  data,
  layoutId,
  monthRange,
}: {
  currency: string;
  data: OwnerStatementSummariesDetailData;
  layoutId: string;
  monthRange: `${string}...${string}`;
}): OwnerStatementSummaryPdfModel => {
  const layout = getOwnerStatementSummariesDetailLayout({
    data,
    layoutId,
  });

  const monthLabel =
    ownerStatementSummariesMonthRangeLabelFormatter(monthRange);
  const resolvedView = data.resolvedView;
  const viewLabel = toTitleCase(resolvedView);

  const ownerName = data.contact.companyType
    ? null
    : formatOwnerName(data.contact as Parameters<typeof formatOwnerName>[0]) ||
      data.contact.name;
  const ownerCompany = data.contact.companyType ? data.contact.name : null;

  const sections = [
    getPdfSection({
      section: layout.netRevenueSection,
      viewLabel,
    }),
    ...layout.otherSections.map((section) =>
      getPdfSection({
        section,
        viewLabel,
      })
    ),
    layout.payoutsSection
      ? getPdfSection({
          section: layout.payoutsSection,
          viewLabel,
        })
      : null,
  ].flatMap((section) => (section ? [section] : []));

  return {
    filename: [
      sanitizeFilePart(data.contact.name || ownerName || ownerCompany),
      sanitizeFilePart(monthLabel),
      sanitizeFilePart(layout.layoutName),
    ].join('_'),
    monthLabel,
    subline: `Owner Statement Summary | ${monthLabel}`,
    teamName: data.team.name || 'Property Manager',
    teamLogo: data.team.logo,
    info: {
      listingAddress: data.listing?.address?.full,
      listingName: data.listing?.name,
      ownerFields: [
        getPdfField('Company', ownerCompany),
        getPdfField('Owner', ownerName),
        getPdfField('Address', data.contact.address?.full),
        getPdfField('Email', data.contact.email),
        getPdfField('Phone', data.contact.phone),
      ].filter((field): field is PdfField => !!field),
      teamFields: [
        getPdfField('Address', data.team.billingAddress?.full),
        getPdfField('Email', data.team.email),
        getPdfField('Phone', data.team.phone),
        getPdfField('Tax ID', data.team.taxId),
      ].filter((field): field is PdfField => !!field),
    },
    summary: {
      layoutName: layout.layoutName,
      lines: getSummaryLines({
        currency,
        financials: data.financials,
      }),
      title: monthLabel,
      viewLabel,
    },
    sections,
  };
};
